import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  GraduationCap, Users, Filter, TrendingDown, Clock,
  Target, FileText, Mail, Bot, CalendarCheck,
  ArrowUpRight, CheckCircle2,
} from "lucide-react";

const problems = [
  { icon: Users, text: "High volume of low-quality leads" },
  { icon: Filter, text: "Poor qualification before consultation" },
  { icon: TrendingDown, text: "Drop-off between inquiry and enrollment" },
  { icon: Clock, text: "Inefficient follow-up processes" },
];

const solutionSteps = [
  { icon: Target, text: "Attract the right applicants" },
  { icon: FileText, text: "Qualify leads through structured forms and automation" },
  { icon: Mail, text: "Nurture prospects with email and messaging" },
  { icon: CalendarCheck, text: "Convert interest into booked consultations" },
];

const caseStudies = [
  {
    clientType: "Training Institute",
    problem: "Generating 500+ inquiries per month but only 8% were converting to enrollments. Most leads went cold within 48 hours.",
    solution: "Built a qualification funnel with automated scoring, instant WhatsApp follow-ups, and a structured consultation booking flow.",
    results: ["3x increase in consultation bookings", "Cost per qualified lead reduced by 45%", "Enrollment rate improved from 8% to 22%"],
  },
  {
    clientType: "Language School",
    problem: "Ads were driving traffic but the website had no structured capture mechanism. Leads were lost in generic contact forms.",
    solution: "Designed program-specific landing pages with progressive form capture, automated nurture sequences, and appointment scheduling.",
    results: ["200+ qualified leads/month", "60% reduction in cost per enrollment", "Automated follow-up covering 100% of leads"],
  },
];

export const EducationSection = () => {
  return (
    <section className="py-20 px-4 bg-background">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          className="mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">Education & Training</h2>
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl">
            Institutes and programs that depend on a steady pipeline of qualified applicants.
          </p>
        </motion.div>

        {/* Two columns: Problems + Solution */}
        <div className="grid md:grid-cols-2 gap-8 mb-14">
          <motion.div
            className="p-6 md:p-8 rounded-2xl bg-card border border-border"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl font-bold text-foreground mb-6">Common Problems</h3>
            <div className="space-y-4">
              {problems.map((p, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-destructive/10 flex items-center justify-center flex-shrink-0">
                    <p.icon className="w-4 h-4 text-destructive" />
                  </div>
                  <p className="text-muted-foreground pt-1.5">{p.text}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="p-6 md:p-8 rounded-2xl bg-card border border-border"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl font-bold text-foreground mb-3">Our Solution</h3>
            <p className="text-muted-foreground text-sm mb-5">We design systems that:</p>
            <div className="space-y-3">
              {solutionSteps.map((s, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted">
                  <s.icon className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-sm font-medium text-foreground">{s.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Case Studies */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl font-bold text-foreground mb-6">Real Results</h3>
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {caseStudies.map((cs, i) => (
              <div key={i} className="p-6 rounded-2xl bg-card border border-border hover:shadow-lg transition-shadow">
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium mb-4">
                  <ArrowUpRight className="w-3.5 h-3.5" />
                  {cs.clientType}
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Problem</p>
                    <p className="text-sm text-foreground">{cs.problem}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Solution</p>
                    <p className="text-sm text-foreground">{cs.solution}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Results</p>
                    <div className="space-y-2">
                      {cs.results.map((r, j) => (
                        <div key={j} className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                          <span className="text-sm font-medium text-foreground">{r}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Link to="/demo">
            <Button size="lg" className="text-base px-8 py-6">
              Get a Free Audit
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
